import { useEffect, useState, type ReactNode } from 'react';
import { ExternalLink, FileText, MessageSquare, X, type LucideIcon } from 'lucide-react';
import type { SourceTarget } from '../../lib/brief-source-target';
import {
  briefCardExits,
  sourceExitLabel,
  EXIT_LABEL,
  type BriefExit,
  type BriefSourceExitTarget,
} from '../../lib/brief-card-exits';

/*
 * RdCardDialog — the mock's universal expandable-card popup (Create Functional Brief App.tsx
 * `CardDialog`), rebuilt on REAL rails for the 2026-07-26 mock-alignment pass.
 *
 * What deliberately did NOT survive the port from the mock:
 *  - `message` toast actions (every mock verb faked success with a canned line). Every action
 *    here is a real handler — usually a navigation — or it doesn't render. Honest verbs only.
 *  - `paths` (the resolution fork) — conflicts keep their own richer workspace
 *    (ConflictResolveModal); this dialog is the KNOW-not-DO drill-in for cards that expand.
 *  - nested `item.open` drill-ins — one level is enough for the data the brief carries today.
 *
 * Visual anatomy matches the approved dialog register (same as ConflictResolveModal): dim
 * cream-tinted scrim, rounded-[26px] popover panel, icon chip + DM Mono eyebrow + title header,
 * bordered item rows, an action-tile grid, footer with Done.
 *
 * Color (bug-615): text that CARRIES the content — description, row body/meta/chip, item note/tag,
 * the picker's site line, the "Open the note" control — is `text-prose-foreground`. Dialog CHROME
 * (eyebrow, section labels, close button) stays `text-muted-foreground`. The two are separate
 * tokens on purpose: muted measures 3.45:1 on this panel and 3.55:1 on the hardcoded #fffaf3 row and
 * item shells in the cream default theme, under the 4.5:1 AA floor — and these popouts are the only
 * place several of these strings render anywhere in the product.
 *
 * Phase-B §B1 added two optional pieces:
 *  - `body` — the composed, popout-sized summary of the item's own note, fetched on open.
 *  - `primary` — the exits. Ruling C1 (2026-08-02) made this UNIVERSAL: every card in the brief
 *    carries it, so the same click always means the same thing. `actions` is now only for verbs a
 *    particular card genuinely has BEYOND the contract (the ledger's second position). The tail
 *    sheet's "+N more · on your Map" tile used to live here and is GONE (phase-4 GAP 1): it
 *    advertised rows that now reveal in place, and it pointed at the Map — a note-node canvas with
 *    no brief-body renderer, so it was the one exit that could not show what it promised.
 */

export interface RdCardItem {
  title: string;
  note?: string | null;
  tag?: string | null;
  // Mock-alignment phase 2 (§8 traceability): the item's REAL source deep link (Slack thread
  // permalink / Gmail thread / GitHub PR). When present the row grows an "Open in source ↗"
  // anchor — an honest navigation, never a faked verb.
  href?: string | null;
  // Terum note PK — when present alongside onNavNote, the row offers "Open the note" in-app.
  conversationId?: string | null;
}

/*
 * A LIST row (phase-B B2, D5) — the shape behind the General section's "N more" sheet: a heading,
 * a body line, and a chip. Deliberately NOT RdCardItem: that shape is a card's provenance/citation
 * row (title + note + "Open in source ↗"), and a sheet of twenty of those reads as twenty cards.
 * A row whose conversationId is set opens the note — the same navigation the band cards offer, so
 * the expansion is a real way into the material, not a receipt the reader can only look at.
 */
export interface RdCardRow {
  title: string;
  body?: string | null;
  chip?: string | null;
  meta?: string | null;
  conversationId?: string | null;
}

export interface RdCardAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
}

/*
 * The UNIVERSAL CLICK CONTRACT (ruling C1, walk 2026-08-02) — [Open the note] + [Open in source ↗]
 * + [Ask], derived by briefCardExits (lib/brief-card-exits.ts, which owns the rules and the
 * Ryan-gated wording). Every card in the brief carries this block: the General band cards, the
 * priority column, the source panels, the decision ledger and the stats board.
 *
 * This supersedes phase-B walk D4's "[Source] always renders" shape. D4 made Source undead by
 * falling back to our own compaction; under C1 that fallback is a second button doing exactly what
 * [Open the note] does, so Source is external-only now and [Ask] — which needs no ids — is what
 * guarantees the block is never empty (the structural half of bug-614's fix).
 */
export interface RdCardPrimary {
  /** Every place the item appeared. Url-less entries are note targets, not sources — see the lib. */
  sourceTargets?: SourceTarget[] | null;
  /** A single external artifact for cards with a url but no citation list (priority tasks). */
  externalUrl?: string | null;
  noteConversationId: string | null;
  askPrefill: string;
  onNavNote: (conversationId: string) => void;
  onAsk: (prefill: string) => void;
}

export interface RdCardConfig {
  icon: ReactNode;
  eyebrow: string;
  title: string;
  description?: string | null;
  // The composed, popout-sized body (phase-B §B1). A ReactNode so it can fetch and compose itself on
  // open (BriefPopoutBody) without this dialog knowing anything about the brief's data layer.
  body?: ReactNode;
  itemsLabel?: string;
  items?: RdCardItem[];
  /*
   * Measured rows before scroll (phase-B §B2/§B3). When set, only this many `items` rows render and
   * the rest sit behind a "+N more"; omitted = the whole list renders, which is what the drill-ins
   * whose lists are short by construction (the ledger's two positions) still want. See CardItems.
   */
  itemsCap?: number | null;
  // B2: the list variant (see RdCardRow). Additive and independent of `items` — a dialog is either
  // a card drill-in (items) or a sheet of rows; nothing renders both today.
  rows?: RdCardRow[];
  /*
   * Measured rows before scroll, the `itemsCap` rule applied to the row list (phase-4 GAP 1). It is
   * a COLLAPSED-PREVIEW count, never a reachability cap: the rest reveals in place behind a
   * "+N more" and the area scrolls, so every row the caller passes stays openable. Omitted = the
   * whole list renders in normal flow, byte-identical to the pre-cap behaviour.
   *
   * Why this had to move off the call site: the General tail sheet used to `slice()` its rows
   * before handing them over, and every row is now a DOOR (spec §12 gave tail rows the ruled
   * dialog with their stored body). A slice therefore deleted the only opener for every stored
   * body past the cap — 9 of 35 on the 2026-08-08 brief, and unbounded on a busier day, since the
   * tail itself is uncapped server-side. A cap that lives HERE cannot strand payload: the widest
   * it can do is ask for one click first.
   */
  rowsCap?: number | null;
  actionsLabel?: string;
  actions: RdCardAction[];
  // The fixed three. Present on brief object popouts; absent on the panels/ledger/stats drill-ins,
  // which keep their own honest verb tiles in `actions`.
  primary?: RdCardPrimary;
  // Phase 2: in-app note navigation for item rows carrying a conversationId (panels drill-ins).
  onNavNote?: (conversationId: string) => void;
}

const TILE_CLASS =
  'flex min-h-20 flex-col items-start justify-between rounded-xl border border-[#e1d7c8] bg-[#fffaf3] p-3 text-left transition hover:border-acc hover:bg-acc-soft dark:border-border dark:bg-muted';

/**
 * The contract's exits, plus [Open in source ↗]'s picker.
 *
 * One live artifact opens directly; several toggle a picker listing each place the item appeared;
 * none means the exit does not render at all (the note exit already goes to our own compaction).
 * The picker is an in-popout list, deliberately not a second overlay: this dialog owns Escape, and
 * a nested overlay would take the whole popout down with it.
 *
 * The picker renders in NORMAL FLOW, below the tile grid — not as an absolutely-positioned drop-up.
 * The dialog body is `overflow-y-auto` inside an `overflow-hidden` panel, and content positioned
 * above a scroll container's content origin is clipped AND unreachable by scrolling: with three or
 * more targets on a card whose body has not composed yet, the picker's top rows were simply
 * invisible. In flow it grows the scrollable height instead, so the worst case is a scroll, never a
 * lost row. That is also why the state lives here rather than inside a Source-only component — the
 * list has to be a sibling of the grid, not a child of one 3-column cell.
 */
const EXIT_ICON: Record<BriefExit['kind'], LucideIcon> = {
  note: FileText,
  source: ExternalLink,
  ask: MessageSquare,
};

// Written out rather than interpolated: Tailwind scans source text, and `grid-cols-${n}` would emit
// no utility at all. The contract yields 1-3 exits, so these are the whole space.
const EXIT_COLS = ['grid-cols-1', 'grid-cols-1', 'grid-cols-2', 'grid-cols-3'] as const;

function PrimaryActions({ primary }: { primary: RdCardPrimary }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const exits = briefCardExits({
    conversationId: primary.noteConversationId,
    externalUrl: primary.externalUrl,
    sourceTargets: primary.sourceTargets,
    askPrefill: primary.askPrefill,
  });
  const targets = exits.find(e => e.kind === 'source')?.targets ?? [];
  const hasPicker = targets.length > 1;
  const go = (t: BriefSourceExitTarget) => {
    setPickerOpen(false);
    window.open(t.href, '_blank', 'noreferrer');
  };
  const run = (exit: BriefExit) => {
    if (exit.kind === 'note') primary.onNavNote(exit.conversationId as string);
    else if (exit.kind === 'ask') primary.onAsk(exit.prefill as string);
    else if (hasPicker) setPickerOpen(v => !v);
    else go(targets[0]);
  };

  return (
    <>
      <div className={`mt-3 grid gap-2 ${EXIT_COLS[Math.min(exits.length, 3)]}`}>
        {exits.map(exit => {
          const Icon = EXIT_ICON[exit.kind];
          const picker = exit.kind === 'source' && hasPicker;
          return (
            <button
              key={exit.kind}
              type="button"
              className={TILE_CLASS}
              aria-expanded={picker ? pickerOpen : undefined}
              aria-haspopup={picker ? 'menu' : undefined}
              onClick={() => run(exit)}
            >
              <Icon size={17} className="text-acc" />
              <span className="text-xs font-medium leading-4">
                {exit.kind === 'source' ? sourceExitLabel(targets.length) : exit.label}
              </span>
            </button>
          );
        })}
      </div>
      {hasPicker && pickerOpen && (
        <ul
          role="menu"
          className="mt-2 overflow-hidden rounded-xl border border-[#e1d7c8] bg-[#fffaf3] dark:border-border dark:bg-muted"
        >
          {targets.map((t, i) => (
            <li key={`${t.href}-${i}`} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => go(t)}
                className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition hover:bg-muted dark:hover:bg-card"
              >
                <span className="text-sm leading-5">{t.label}</span>
                {t.site && (
                  <span className="font-['DM_Mono'] text-[10px] text-prose-foreground">{t.site}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/*
 * The item list, in phase-B §B2/§B3's measured-rows-before-scroll shape.
 *
 * `cap` rows render; the remainder sits behind a "+N more" that reveals it IN PLACE — the same
 * dialog, never a second overlay, for the same reason the source picker above is in-flow: this
 * dialog owns Escape, and a nested overlay would take the whole popout down with it. Once revealed,
 * the list area caps its own height and scrolls, so a 23-row panel drill-in (shipped) can no longer
 * push the card's own exits and Done off the bottom of a 92vh panel. Both routes to the overflow
 * therefore exist at once: expand, and scroll.
 *
 * No cap ⇒ every row renders in normal flow, byte-identical to the pre-cap behaviour — the drill-ins
 * whose lists are short by construction are deliberately left alone.
 *
 * Expansion state resets because the dialog UNMOUNTS between opens (every call site renders it as
 * `{openCard && <RdCardDialog …/>}` and the scrim makes closing the only way to reach another card),
 * so there is no stale-expansion window to guard.
 */
function CardItems({
  items,
  cap,
  label,
  onNavNote,
}: {
  items: RdCardItem[];
  cap?: number | null;
  label?: string;
  onNavNote?: (conversationId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const overflow = cap != null ? Math.max(items.length - cap, 0) : 0;
  const visible = cap != null && !expanded && overflow > 0 ? items.slice(0, cap) : items;

  return (
    <>
      {/* No default label. The two that used to live here ('In detail' / 'Go deeper') were
        * never passed by anything that wanted them, so removing the four call-site props
        * would have silently promoted the defaults into the popout instead of deleting the
        * labels (phase-B walk D2). A section without a label simply has none. */}
      {label && (
        <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-muted-foreground">{label}</p>
      )}
      <div
        id="rd-card-items"
        /* max-h is half the dialog's own 92vh envelope: enough that an expanded list still reads as
         * a list, little enough that the exits below it stay reachable without hunting. */
        className={`${label ? 'mt-3 ' : ''}space-y-2${cap != null ? ' max-h-[46vh] overflow-y-auto' : ''}`}
      >
        {visible.map((item, i) => (
          <article
            key={`${item.title}-${i}`}
            className="rounded-2xl border border-[#e4dacb] bg-[#fffaf3] p-4 dark:border-border dark:bg-muted"
          >
            <span className="flex items-start justify-between gap-3">
              <span className="text-sm font-semibold">{item.title}</span>
              {item.tag && (
                <span className="shrink-0 rounded-md bg-muted px-1.5 py-1 font-['DM_Mono'] text-[9px] text-prose-foreground dark:bg-card">
                  {item.tag}
                </span>
              )}
            </span>
            {item.note && <span className="mt-1 block text-sm leading-5 text-prose-foreground">{item.note}</span>}
            {(item.href || (item.conversationId && onNavNote)) && (
              <span className="mt-2 flex items-center gap-3">
                {item.href && (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-acc transition hover:opacity-80"
                  >
                    {EXIT_LABEL.source}
                  </a>
                )}
                {item.conversationId && onNavNote && (
                  <button
                    type="button"
                    onClick={() => onNavNote(item.conversationId as string)}
                    className="text-xs font-medium text-prose-foreground transition hover:text-foreground"
                  >
                    {EXIT_LABEL.note}
                  </button>
                )}
              </span>
            )}
          </article>
        ))}
      </div>
      {/* Same toggle grammar as the brief's own in-place expanders (the coda, the suppression
        * receipt): the count, then the direction glyph. */}
      {overflow > 0 && (
        <button
          type="button"
          data-testid="rd-card-items-more"
          aria-expanded={expanded}
          aria-controls="rd-card-items"
          className="mt-3 flex items-center gap-1 text-xs font-medium text-acc transition hover:text-acc-hi"
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? 'Show fewer' : `+${overflow} more`}
          <span aria-hidden="true">{expanded ? '↑' : '↓'}</span>
        </button>
      )}
    </>
  );
}

/*
 * The ROW list (phase-B §B2's sheet), with CardItems' measured-rows-before-scroll treatment
 * (phase-4 GAP 1). Same grammar, same reasons, deliberately a sibling rather than a shared
 * generic: an item is a card's provenance row and a row is a sheet entry, and the two carry
 * different fields and different destinations (see RdCardItem vs RdCardRow).
 *
 * `cap` rows render; the remainder reveals IN PLACE behind a "+N more" inside a max-h scroll area.
 * Both routes to the overflow exist at once — expand, and scroll — so a 13-row General tail is
 * fully reachable without the sheet growing to 4.7 screens (the defect the original cap was cut
 * for). No cap ⇒ every row renders in normal flow.
 *
 * Expansion state resets because the dialog UNMOUNTS between opens (every call site renders it as
 * `{openCard && <RdCardDialog …/>}`), so there is no stale-expansion window to guard.
 */
function CardRows({
  rows,
  cap,
  onNavNote,
}: {
  rows: RdCardRow[];
  cap?: number | null;
  onNavNote?: (conversationId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const overflow = cap != null ? Math.max(rows.length - cap, 0) : 0;
  const visible = cap != null && !expanded && overflow > 0 ? rows.slice(0, cap) : rows;

  return (
    <>
      <div
        id="rd-card-rows"
        data-testid="rd-card-rows"
        /* max-h matches CardItems: half the dialog's own 92vh envelope, so an expanded sheet still
         * reads as a list while the footer's Done stays reachable without hunting. */
        className={`space-y-2${cap != null ? ' max-h-[46vh] overflow-y-auto' : ''}`}
      >
        {visible.map((row, i) => {
          const open = row.conversationId && onNavNote
            ? () => onNavNote?.(row.conversationId as string)
            : null;
          const body = (
            <>
              <span className="flex items-start justify-between gap-3">
                <span className="text-sm font-semibold">{row.title}</span>
                {row.chip && (
                  <span className="shrink-0 rounded-md bg-muted px-1.5 py-1 font-['DM_Mono'] text-[9px] text-prose-foreground dark:bg-card">
                    {row.chip}
                  </span>
                )}
              </span>
              {row.body && <span className="mt-1 block text-sm leading-5 text-prose-foreground">{row.body}</span>}
              {row.meta && <span className="mt-2 block text-xs text-prose-foreground">{row.meta}</span>}
            </>
          );
          const shell = 'block w-full rounded-2xl border border-[#e4dacb] bg-[#fffaf3] p-4 text-left dark:border-border dark:bg-muted';
          return open ? (
            <button key={`${row.title}-${i}`} type="button" onClick={open} className={`${shell} transition hover:border-acc`}>
              {body}
            </button>
          ) : (
            <article key={`${row.title}-${i}`} className={shell}>
              {body}
            </article>
          );
        })}
      </div>
      {/* Same toggle grammar as CardItems and the brief's own in-place expanders (the coda, the
        * suppression receipt): the count, then the direction glyph. */}
      {overflow > 0 && (
        <button
          type="button"
          data-testid="rd-card-rows-more"
          aria-expanded={expanded}
          aria-controls="rd-card-rows"
          className="mt-3 flex items-center gap-1 text-xs font-medium text-acc transition hover:text-acc-hi"
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? 'Show fewer' : `+${overflow} more`}
          <span aria-hidden="true">{expanded ? '↑' : '↓'}</span>
        </button>
      )}
    </>
  );
}

export function RdCardDialog({ config, onClose }: { config: RdCardConfig; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="rd-card-title"
      className="rd fixed inset-0 z-50 flex items-end justify-center bg-[#2d2822]/35 p-3 font-['DM_Sans'] text-foreground backdrop-blur-[2px] sm:items-center sm:p-6"
      onMouseDown={onClose}
    >
      <section
        onMouseDown={e => e.stopPropagation()}
        className="rd-card-dialog flex max-h-[92vh] w-full max-w-[620px] flex-col overflow-hidden rounded-[26px] border border-[#ded2c0] bg-popover shadow-[0_28px_80px_rgba(48,38,25,.25)] dark:border-border"
      >
        <div className="border-b border-[#e6dccd] px-5 pb-5 pt-5 sm:px-7 sm:pt-7 dark:border-border">
          <div className="flex items-start justify-between gap-5">
            <div className="flex items-start gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-acc-soft text-acc">
                {config.icon}
              </span>
              <div>
                <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">
                  {config.eyebrow}
                </p>
                {/* Card titles are DM Sans, never Instrument Serif (Teddy 2026-07-30): the serif is
                  * display-only — page mastheads and section headings — and a card title routinely
                  * carries model-composed prose, which reads as decoration in a display face. */}
                <h2 id="rd-card-title" className="mt-1 text-[19px] font-semibold leading-snug tracking-[-.01em]">
                  {config.title}
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close dialog"
              className="grid size-9 shrink-0 place-items-center rounded-full border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              <X size={17} />
            </button>
          </div>
          {config.description && (
            <p className="mt-4 max-w-[520px] text-[15px] leading-6 text-prose-foreground">{config.description}</p>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-7">
          {config.rows && config.rows.length > 0 && (
            <div>
              <CardRows rows={config.rows} cap={config.rowsCap} onNavNote={config.onNavNote} />
            </div>
          )}
          {/* P7 — the body zone owns its OWN trailing hairline and collapses WHOLE when the composed
            * body renders nothing. `config.body` is a ReactNode, so it stays truthy even when
            * BriefPopoutBody resolves to null (compose failed, or the note carried no sections);
            * keying the NEXT zone's divider off that truthiness framed ~41px of empty space between
            * two hairlines. `:empty` is the only signal that tracks what actually RENDERED — the body
            * composes asynchronously, after this dialog last rendered.
            * Geometry is unchanged when a body does render: pb-5 + 1px + mb-5 is the same 41px, in the
            * same place, as the mt-5 + 1px + pt-5 it replaces.
            * INVARIANT: a `body` node must not wrap its content in an element that renders
            * unconditionally, or this zone is never `:empty` (see openTaskCard in BriefSurface). */}
          {config.body && (
            <div
              data-testid="popout-body-zone"
              className="mb-5 border-b border-[#e6dccd] pb-5 empty:hidden dark:border-border"
            >
              {config.body}
            </div>
          )}
          {config.items && config.items.length > 0 && (
            <div>
              <CardItems
                items={config.items}
                cap={config.itemsCap}
                label={config.itemsLabel}
                onNavNote={config.onNavNote}
              />
            </div>
          )}
          {config.primary && (
            <div
              className={
                config.items && config.items.length > 0
                  ? 'mt-5 border-t border-[#e6dccd] pt-5 dark:border-border'
                  : ''
              }
            >
              <PrimaryActions primary={config.primary} />
            </div>
          )}
          {config.actions.length > 0 && (
            <div className={config.items && config.items.length > 0 ? 'mt-5 border-t border-[#e6dccd] pt-5 dark:border-border' : ''}>
              {config.actionsLabel && (
                <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-muted-foreground">
                  {config.actionsLabel}
                </p>
              )}
              <div className={`${config.actionsLabel ? 'mt-3 ' : ''}grid grid-cols-2 gap-2 sm:grid-cols-4`}>
                {config.actions.map(({ label, icon: Icon, onClick }) => (
                  <button
                    key={label}
                    onClick={onClick}
                    className="flex min-h-20 flex-col items-start justify-between rounded-xl border border-[#e1d7c8] bg-[#fffaf3] p-3 text-left transition hover:border-acc hover:bg-acc-soft dark:border-border dark:bg-muted"
                  >
                    <Icon size={17} className="text-acc" />
                    <span className="text-xs font-medium leading-4">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end border-t border-[#e6dccd] bg-[#f6f0e6] px-5 py-4 sm:px-7 dark:border-border dark:bg-muted">
          <button
            onClick={onClose}
            className="rounded-lg bg-foreground px-4 py-2 text-sm text-card transition hover:opacity-90"
          >
            Done
          </button>
        </div>
      </section>
    </div>
  );
}
